import { supabase } from '../lib/supabase'
import {
  getOfflineAttachments,
  getOfflineQueue,
  removeOfflineActivity,
  updateOfflineActivity,
  type OfflineActivity,
  type OfflineAttachment
} from '../utils/offlineQueue'
import {
  OFFLINE_ACTIVITY_TABLE,
  hasOfflineAttachments,
  isOfflineUpdate,
} from '../core/database'
import {
  createInvalidOfflineUpdateError,
  createOfflineTypeError,
} from '../core/errors'
import { EVENT_TYPES } from '../core/events'
import { measureRuntimeMetric } from '../core/telemetry'

type OperationPayload = Record<string, unknown>
type UpdatePayload = { id?: unknown; dados?: unknown }

const MAX_SYNC_ATTEMPTS = 5
const MIN_SYNC_INTERVAL = 15000
const SLOW_NETWORK_INTERVAL = 90000

let syncInProgress = false
let lastSyncStartedAt = 0

function isSlowConnection() {
  const nav = navigator as Navigator & {
    connection?: {
      effectiveType?: string
      saveData?: boolean
    }
  }

  const connection = nav.connection
  if (!connection) return false

  return connection.saveData === true || ['slow-2g', '2g'].includes(connection.effectiveType ?? '')
}

function shouldDebounceSync() {
  const now = Date.now()
  const minInterval = isSlowConnection() ? SLOW_NETWORK_INTERVAL : MIN_SYNC_INTERVAL
  return now - lastSyncStartedAt < minInterval
}

async function uploadOfflineAttachment(attachment: OfflineAttachment) {
  const { error } = await supabase.storage
    .from(attachment.bucket)
    .upload(attachment.path, attachment.blob, {
      contentType: attachment.mimeType,
      upsert: true
    })

  if (error) throw error

  return attachment.path
}

async function uploadActivityAttachments(activity: OfflineActivity) {
  const attachments = await getOfflineAttachments(activity.id)
  const uploaded: Record<string, string> = {}

  for (const attachment of attachments) {
    const uploadedPath = await uploadOfflineAttachment(attachment)
    uploaded[attachment.fieldName] = uploadedPath
  }

  return uploaded
}

async function syncInsert(table: string, activity: OfflineActivity) {
  const attachmentFields = hasOfflineAttachments(activity.type)
    ? await uploadActivityAttachments(activity)
    : {}

  const payload = {
    ...(activity.payload as OperationPayload),
    ...attachmentFields,
  }

  const { error } = await supabase.from(table).insert(payload)

  if (error) throw error
}

async function syncUpdate(table: string, activity: OfflineActivity) {
  const payload = activity.payload as UpdatePayload

  if (typeof payload.id !== 'string' || !payload.id) {
    throw createInvalidOfflineUpdateError('id ausente ou invalido')
  }

  if (!payload.dados || typeof payload.dados !== 'object' || Array.isArray(payload.dados)) {
    throw createInvalidOfflineUpdateError('dados ausentes ou invalidos')
  }

  const { error } = await supabase
    .from(table)
    .update(payload.dados as Record<string, unknown>)
    .eq('id', payload.id)

  if (error) throw error
}

async function processActivity(activity: OfflineActivity) {
  const table = OFFLINE_ACTIVITY_TABLE[activity.type]

  if (!table) {
    throw createOfflineTypeError(activity.type)
  }

  if (isOfflineUpdate(activity.type)) {
    return syncUpdate(table, activity)
  }

  return syncInsert(table, activity)
}

export async function syncPendingOfflineActivities() {
  return measureRuntimeMetric(
    EVENT_TYPES.sincronizacaoConcluida,
    async () => {
      if (!navigator.onLine) {
        const queue = await getOfflineQueue()
        return { synced: 0, pending: queue.length }
      }

      if (syncInProgress || shouldDebounceSync()) {
        const queue = await getOfflineQueue()
        return { synced: 0, pending: queue.length }
      }

      syncInProgress = true
      lastSyncStartedAt = Date.now()

      try {
        const queue = await getOfflineQueue()
        let synced = 0

        for (const activity of queue) {
          if (activity.attempts >= MAX_SYNC_ATTEMPTS) continue

          try {
            await updateOfflineActivity({
              ...activity,
              syncStatus: 'sincronizando'
            })

            await processActivity(activity)

            await removeOfflineActivity(activity.id)

            synced += 1
          } catch (error) {
            await updateOfflineActivity({
              ...activity,
              syncStatus: 'erro',
              attempts: activity.attempts + 1,
              lastError: error instanceof Error
                ? error.message
                : 'Erro desconhecido'
            })
          }
        }

        const pendingQueue = await getOfflineQueue()

        return {
          synced,
          pending: pendingQueue.length
        }
      } finally {
        syncInProgress = false
      }
    },
    {
      source: 'offline_sync'
    }
  )
}

export function startOfflineAutoSync() {
  const run = () => {
    syncPendingOfflineActivities().catch((error) => {
      console.error('Falha na sincronizacao offline', error)
    })
  }

  window.addEventListener('online', run)

  const intervalId = window.setInterval(() => {
    if (navigator.onLine) run()
  }, isSlowConnection() ? SLOW_NETWORK_INTERVAL : 120000)

  if (navigator.onLine) run()

  return () => {
    window.removeEventListener('online', run)
    window.clearInterval(intervalId)
  }
}
